// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';
import { UserAuthState } from '@interfaces/user.interfaces';
import { Observable, of, switchMap } from 'rxjs';
import { catchError, filter, map, take, tap } from 'rxjs/operators';
import { AuthService } from '../../login/services/auth.service';
import { UserService } from '@services/user.service';
import { RouteGuardService } from '@services/route-guard.service';

@Injectable({
    providedIn: 'root',
})
export class AuthGuard implements CanActivate {
    constructor(
        private readonly authService: AuthService,
        private readonly userService: UserService,
        private readonly routeGuardService: RouteGuardService
    ) {}

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        this.authService.checkAuthState();
        this.authService.setRedirectUrl(state.url).subscribe();

        return this.authService.isAuthenticated$().pipe(
            // only authenticated
            filter((isAuthenticated) => isAuthenticated),
            switchMap((isAuthenticated) => this.userService.userState$()),
            // wait for user to load
            filter((userState) => [UserAuthState.userLoaded, UserAuthState.error, UserAuthState.loggedOut].includes(userState)),
            // canActivate !== error or logged out
            map((userState) => userState === UserAuthState.userLoaded),
            catchError((err) => of(false)),
            tap((canActivate) => {
                this.routeGuardService.logRouteGuard(this.constructor.name, canActivate);
            })
        );
    }
}
